/**
 * Performance telemetry collectors.
 *
 * Everything here answers one of four questions, and anything that answers none of them does not
 * belong (see docs/telemetria-rendimiento.md):
 *   1. Does the app degrade as a shift goes on, and with what does that correlate?
 *   2. How long does the person actually wait — finger to shutter, finger to "Analyse"?
 *   3. What hardware are we running on? (today: unknown)
 *   4. WHO is blocking the main thread?
 *
 * Two rules shape the whole module:
 *
 * - **Measuring must not perturb what it measures.** No permanent `setInterval`, no open
 *   `requestAnimationFrame` chain (chaining rAF forces a BeginMainFrame per vsync that would not
 *   otherwise happen, and a chain nobody cancels IS the permanent sampler we are avoiding). The
 *   observers used here are passive: the browser fills them in whether we look or not.
 * - **Telemetry never throws into the caller.** Every collector is wrapped and returns `null` when
 *   the API is missing. That also makes them testable under happy-dom, which has none of them.
 */
import Logger from '@/logger';

const logger = new Logger({ screen: 'perfTelemetry' });

// Long Animation Frames kept in memory. A capture window spans a handful of frames; the heartbeat
// aggregates over minutes. 120 entries is far more than either needs and bounds the memory.
const LOAF_BUFFER = 120;
// Below this a frame is not worth attributing: it is a normal frame, not a stall. Measured on
// DURATION, never on blockingDuration — see the observer below for why that distinction decides
// whether this whole channel reports anything at all. 50ms is also the threshold at which the
// browser emits a LoAF entry, so in practice nothing is dropped; the constant guards against a
// future browser lowering it and flooding the buffer with ordinary frames.
const LOAF_MIN_DURATION_MS = 50;
// Tombstone: what the previous page left behind. localStorage because it is SYNCHRONOUS — the
// renderer being killed for memory is precisely the case where nothing async survives.
const TOMBSTONE_KEY = 'bronze_perf_tombstone';
const RELOAD_REASON_KEY = 'bronze_perf_reload_reason';
// Deadline for the CPU probe. Deliberately loose: a severely throttled tablet is the case we most
// want a number for, and that is exactly when it will be slowest to answer.
const BENCH_TIMEOUT_MS = 8000;

let loafEntries = [];
let loafObserver = null;

/* --------------------------------------------------------------- long animation frames */

/**
 * Starts the LoAF observer. This is the only signal that says WHO blocked the main thread:
 * each entry carries `scripts[].invoker` (`IDBRequest.onsuccess`, `Worker.onmessage`,
 * `XMLHttpRequest.onprogress`, …) and a `sourceURL`.
 *
 * Deliberately not `longtask`: the camera investigation already established that the contention
 * that hurts this app is made of many sub-50ms tasks, which the long-task API cannot see at all.
 * LoAF measures FRAMES, so a cascade of small tasks that delays a frame does show up.
 */
export function startLoafObserver() {
  if (loafObserver) return true;
  try {
    if (typeof PerformanceObserver === 'undefined') return false;
    const supported = PerformanceObserver.supportedEntryTypes || [];
    if (!supported.includes('long-animation-frame')) return false;
    loafObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // Gate on duration, NOT blockingDuration. Measured on a Lenovo TB336FU and a Honor Pad
        // X9A (Chrome 153, 2026-09-22): every long frame a real capture produced — 53ms, 66ms,
        // and a deliberate 332ms block — arrived with `blockingDuration: 0` and an empty
        // `scripts` array. A blockingDuration gate therefore dropped 100% of entries and this
        // channel reported nothing, which reads in Athena as "nothing ever blocks".
        // The reason is worth keeping: blockingDuration sums, per task, only what exceeds 50ms,
        // so a 66ms frame built from several 20ms tasks scores zero — i.e. it inherits exactly
        // the blindness to sub-50ms cascades that made us choose LoAF over longtask in the first
        // place. blockingDuration is still reported below, as data, next to the duration.
        if ((entry.duration || 0) < LOAF_MIN_DURATION_MS) continue;
        // A LoAF is not only its scripts. Measured on a Honor Pad X9A, `scripts` accounted for just
        // 5-25ms of a 230ms blocking frame: the rest was the browser's own render work (style,
        // layout, paint, and native image decode), which has no invoker to name. Splitting the
        // frame into script / style+layout / the remainder is what tells "our JavaScript is slow"
        // apart from "the device is decoding a 5MB sensor JPEG", and those need opposite fixes.
        const scriptMs = (entry.scripts || []).reduce((t, s) => t + (s.duration || 0), 0);
        const styleMs = entry.styleAndLayoutStart
          ? Math.max(0, entry.startTime + entry.duration - entry.styleAndLayoutStart)
          : 0;
        loafEntries.push({
          start: entry.startTime,
          end: entry.startTime + entry.duration,
          blocking: entry.blockingDuration || 0,
          scriptMs,
          styleMs,
          scripts: (entry.scripts || []).slice(0, 3).map((s) => ({
            invoker: String(s.invoker || s.invokerType || '?').slice(0, 60),
            src: basename(s.sourceURL),
            ms: Math.round(s.duration || 0),
          })),
        });
        if (loafEntries.length > LOAF_BUFFER) loafEntries.shift();
      }
    });
    loafObserver.observe({ type: 'long-animation-frame', buffered: true });
    return true;
  } catch (error) {
    logger.console('LoAF observer unavailable', error);
    return false;
  }
}

export function stopLoafObserver() {
  try {
    loafObserver?.disconnect();
  } catch (e) {
    // Nothing to do on teardown.
  }
  loafObserver = null;
  loafEntries = [];
}

/** Last segment of a script URL — enough to tell our bundle from PostHog, without sending a URL. */
function basename(url) {
  if (!url) return null;
  try {
    return String(url).split('/').pop().split('?')[0].slice(0, 40) || null;
  } catch (e) {
    return null;
  }
}

/**
 * Blocking attributable to a time window, e.g. one capture. Returns null when nothing blocked, so
 * the common (healthy) case adds no fields to the event.
 */
export function loafWindow(fromMs, toMs) {
  try {
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return null;
    const hits = loafEntries.filter((e) => e.end >= fromMs && e.start <= toMs);
    if (hits.length === 0) return null;
    const blocking = hits.reduce((t, e) => t + e.blocking, 0);
    const byInvoker = new Map();
    for (const hit of hits) {
      for (const script of hit.scripts) {
        const key = `${script.invoker}|${script.src || ''}`;
        byInvoker.set(key, (byInvoker.get(key) || 0) + script.ms);
      }
    }
    const top = [...byInvoker.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([key, ms]) => {
        const [invoker, src] = key.split('|');
        return { invoker, src: src || null, ms };
      });
    const scriptMs = Math.round(hits.reduce((t, e) => t + e.scriptMs, 0));
    const styleMs = Math.round(hits.reduce((t, e) => t + e.styleMs, 0));
    const totalMs = Math.round(hits.reduce((t, e) => t + (e.end - e.start), 0));
    return {
      loaf_count: hits.length,
      // The headline is frame TIME, because blocking is routinely 0 on these devices (see the
      // observer). loaf_duration_ms is what the operator actually waited through.
      loaf_duration_ms: totalMs,
      loaf_max_ms: Math.round(Math.max(...hits.map((e) => e.end - e.start))),
      // Kept, and expected to be 0 far more often than not: it is the spec's own measure and the
      // gap between it and loaf_duration_ms is itself the finding.
      loaf_blocking_ms: Math.round(blocking),
      loaf_script_ms: scriptMs,
      loaf_style_ms: styleMs,
      // What neither our scripts nor style/layout explain: browser-internal work such as paint or
      // a native image decode. A large value here means the fix is not in our JavaScript.
      loaf_other_ms: Math.max(0, totalMs - scriptMs - styleMs),
      loaf_top: top,
    };
  } catch (error) {
    return null;
  }
}

/** Aggregate since a given time, for the heartbeat, and drop what is older. */
export function loafSince(fromMs) {
  const out = loafWindow(fromMs, Number.MAX_SAFE_INTEGER);
  loafEntries = loafEntries.filter((e) => e.end >= fromMs);
  return out;
}

/* ------------------------------------------------------------------------ device profile */

/**
 * What hardware this actually is. The model is only reachable through userAgentData: since Chrome
 * 110 the Android user-agent string says `Android 10; K` for every device on earth, so the UA is
 * useless for an inventory. The `ch-ua-model` permission defaults to `self`, so a top-level
 * document gets it without any `Accept-CH` or `Permissions-Policy` header.
 */
export async function collectDeviceProfile(env = window) {
  const nav = env.navigator || {};
  const profile = {
    device_memory: nav.deviceMemory ?? null,
    cores: nav.hardwareConcurrency ?? null,
    screen: safe(() => `${env.screen.width}x${env.screen.height}@${env.devicePixelRatio || 1}`),
    // The only way to tell an iPad from a desktop Mac. Safari on iPadOS reports
    // `Macintosh; Intel Mac OS X 10_15_7` with `platform: MacIntel` and has no userAgentData, so
    // measured on an iPad Pro (iPadOS 26) every other field here is indistinguishable from a
    // laptop's. maxTouchPoints is 5 there and 0 on a Mac. Without it a fleet of iPads would be
    // invisible in Athena, filed under desktop.
    touch_points: nav.maxTouchPoints ?? null,
    standalone: safe(() => !!env.matchMedia?.('(display-mode: standalone)')?.matches),
    offscreen_canvas: typeof env.OffscreenCanvas !== 'undefined',
    net: safe(() => nav.connection?.effectiveType) ?? null,
    model: null,
    platform_version: null,
    chrome_full: null,
  };
  try {
    const high = await nav.userAgentData?.getHighEntropyValues?.(['model', 'platformVersion', 'fullVersionList']);
    if (high) {
      profile.model = high.model || null;
      profile.platform_version = high.platformVersion || null;
      const chrome = (high.fullVersionList || []).find((b) => /Chrome/i.test(b.brand));
      profile.chrome_full = chrome?.version || null;
    }
  } catch (error) {
    // Non-Chromium, or the hint was denied. The rest of the profile is still worth sending.
  }
  return profile;
}

/* ----------------------------------------------------------------------------- tombstone */

/**
 * The renderer dying under memory pressure is the event we most want to count — and it is exactly
 * the one that cannot report itself: no `pagehide`, no unload handler, and the forensic channel's
 * flush is an async POST that never leaves. So each heartbeat (and every hide) writes a synchronous
 * tombstone, and the NEXT load reports what it finds. Same reasoning the ImageCapture crash guard
 * already uses.
 */
export function writeTombstone(state) {
  try {
    localStorage.setItem(TOMBSTONE_KEY, JSON.stringify(state));
  } catch (e) {
    // Storage full or disabled: the tombstone is best-effort by design.
  }
}

export function readAndClearTombstone() {
  try {
    const raw = localStorage.getItem(TOMBSTONE_KEY);
    localStorage.removeItem(TOMBSTONE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/** The app calls this right before a deliberate location.reload(), so a reload is not read as a kill. */
export function noteReloadReason(reason) {
  try {
    sessionStorage.setItem(RELOAD_REASON_KEY, String(reason).slice(0, 40));
  } catch (e) {
    // Best effort.
  }
}

export function takeReloadReason() {
  try {
    const value = sessionStorage.getItem(RELOAD_REASON_KEY);
    sessionStorage.removeItem(RELOAD_REASON_KEY);
    return value;
  } catch (e) {
    return null;
  }
}

/**
 * Turns the previous page's tombstone into the fields of `fx_app_start`. `prev_end` is the whole
 * point: 'clean' means the page said goodbye, 'killed' means it did not — which, with
 * `prev_photos` and `prev_since_load_ms`, is a measurable rate of "the tablet had to be reloaded"
 * per device model.
 */
export function appStartProps(env = window) {
  const tomb = readAndClearTombstone();
  const reloadReason = takeReloadReason();
  return {
    nav_type: safe(() => env.performance.getEntriesByType('navigation')[0]?.type) ?? null,
    was_discarded: safe(() => env.document.wasDiscarded) ?? null,
    reload_reason: reloadReason,
    prev_page_id: tomb?.page_id ?? null,
    prev_since_load_ms: tomb?.since_load_ms ?? null,
    prev_photos: tomb?.photos ?? null,
    prev_end: tomb ? (tomb.clean ? 'clean' : 'killed') : 'none',
    prev_age_ms: tomb?.ts ? Math.max(0, Date.now() - tomb.ts) : null,
  };
}

/* -------------------------------------------------------------------------- misc probes */

/**
 * Bytes IndexedDB is holding. Grows with photos waiting to upload; if it grows while the queue is
 * empty, there are orphan rows — a different bug with the same symptom.
 */
export async function storageUsageMb(env = window) {
  try {
    const estimate = await env.navigator?.storage?.estimate?.();
    return estimate?.usage ? Math.round(estimate.usage / 1048576) : null;
  } catch (e) {
    return null;
  }
}

/**
 * JS heap, as rough context only. NOT the degradation signal: it excludes Blobs, ImageBitmaps,
 * canvases and GPU textures — i.e. everything we actually suspect — and without crossOriginIsolated
 * the browser quantises it. Kept because it is free and occasionally corroborates; never relied on.
 */
export function heapMb(env = window) {
  try {
    const used = env.performance?.memory?.usedJSHeapSize;
    return used ? Math.round(used / 1048576) : null;
  } catch (e) {
    return null;
  }
}

export function domNodes(env = window) {
  return safe(() => env.document.getElementsByTagName('*').length) ?? null;
}

/**
 * How long a FIXED unit of work takes right now — the only thermal-throttling signal reachable
 * from a web page on these tablets. See cpuBench.worker.js for why `PressureObserver` cannot be
 * used (it does not exist on Android Chrome) and why the workload is a JPEG encode.
 *
 * Read it as a ratio, not an absolute: a device's own baseline is the reference, and a value 2-4x
 * its usual figure means the SoC has been throttled, not that the app got slower. Absolute values
 * are only comparable between identical models.
 *
 * Returns null rather than throwing wherever the worker cannot run.
 */
export function cpuBenchMs(env = window) {
  return new Promise((resolve) => {
    let worker = null;
    let timer = null;
    const finish = (value) => {
      clearTimeout(timer);
      try { worker?.terminate(); } catch (e) { /* already gone */ }
      resolve(value);
    };
    try {
      if (typeof env.Worker === 'undefined' || typeof env.OffscreenCanvas === 'undefined') {
        resolve(null);
        return;
      }
      worker = new Worker(new URL('./cpuBench.worker.js', import.meta.url));
      // A throttled device is slow by definition, so the deadline has to be generous — but a
      // probe that never answers must not leak a worker into the next beat.
      timer = setTimeout(() => finish(null), BENCH_TIMEOUT_MS);
      worker.onmessage = (event) => finish(event.data?.ms ?? null);
      worker.onerror = () => finish(null);
      worker.onmessageerror = () => finish(null);
      worker.postMessage({ id: 1 });
    } catch (error) {
      logger.console('cpu bench unavailable', error);
      finish(null);
    }
  });
}

function safe(fn) {
  try {
    const value = fn();
    return value === undefined ? null : value;
  } catch (e) {
    return null;
  }
}
